import { useQueryClient } from "@tanstack/react-query";
import { FileUser, LayoutDashboard, RotateCcw, Settings2, Users } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  FEATHER_APPLICANT_PATH,
  FEATHER_ATS_PATH,
  FEATHER_DEMO_SETUP_PATH,
  FEATHER_VOUCHER_PATH,
  featherLiveCheckpointPath,
} from "@/lib/featherIntegration";
import { useDemoRunSession } from "@/hooks/useDemoRunSession";
import { useVoucherInvitations } from "@/hooks/useVoucherInvitations";
import { clearDemoRun, lastSentVoucher, readDemoRun } from "@/lib/featherDemoRun";
import { FEATHER_PARTNER_NAME } from "@/lib/featherReview";

const VOUCHER_TAB_REASON = {
  no_run: "Start a run first",
  none_sent: "Send a vouch request first",
  link_not_in_tab: "Open the invite email to continue as the voucher",
  vouch_received: "The vouch has been received",
} as const;

/**
 * The preview bar on every /feather page. Mounted by `AppShell` only for a
 * signed-in viewer on a /feather path in a non-production build, so the
 * voucher link it opens is never reachable from /verify/:token, where the
 * applicant is.
 */
export function FeatherFlowSwitcher() {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // Re-read on every navigation (the component re-renders on each), so a run
  // started a moment ago shows up without any shared state.
  const run = readDemoRun();
  // The same react-query entry the applicant page writes when it sends or
  // resends a vouch request, so the link is there without being stored. A
  // reload empties it, and the tab says to use the invite email instead.
  const { invitations } = useVoucherInvitations(run?.token);
  const voucher = run ? lastSentVoucher(invitations) : null;
  // Already in the voucher journey: stay put. After activation the page has
  // swapped the invite token for the voucher session token, and linking back
  // to the invite token would activate it a second time.
  const inVoucherJourney = pathname === FEATHER_VOUCHER_PATH && new URLSearchParams(search).has("token");
  // The current URL without an OAuth return's `oauth` and `status`: following
  // the tab must not replay "you just signed in" (UAT walk, 2026-10-14).
  const currentJourney = (() => {
    const params = new URLSearchParams(search);
    params.delete("oauth");
    params.delete("status");
    return `${pathname}?${params.toString()}`;
  })();
  const voucherHref = inVoucherJourney
    ? currentJourney
    : voucher?.token
      ? `${FEATHER_VOUCHER_PATH}?token=${encodeURIComponent(voucher.token)}`
      : null;
  const voucherReason = inVoucherJourney
    ? null
    : !run
    ? VOUCHER_TAB_REASON.no_run
    : voucher?.noLinkReason
      ? VOUCHER_TAB_REASON[voucher.noLinkReason]
      : null;
  // The ATS view shows this run's Case once the applicant has submitted.
  const { submitted, ended } = useDemoRunSession(run?.token);
  const atsReason = !run
    ? "Start a run first"
    : submitted
      ? null
      : ended
        ? "This run's application was closed before it was submitted. Start a new run"
        : "Available once the applicant submits";

  // Back to the start of the demo: forget this tab's run and go to the
  // landing page, whose Continue starts a fresh one. The presenter's details
  // (demo setup) are kept. The Case itself stays in the org (no cleanup,
  // Juan 2026-10-13).
  const resetDemo = () => {
    clearDemoRun();
    queryClient.removeQueries({ queryKey: ["feather-demo"] });
    navigate(FEATHER_APPLICANT_PATH);
  };

  const views = [
    // With a run in this tab, the applicant view is that run's live journey;
    // the landing page (and a new run) is one link away from it.
    { href: run ? featherLiveCheckpointPath(run.token) : FEATHER_APPLICANT_PATH, label: "Applicant flow", icon: FileUser },
    // The live voucher journey for the last vouch request sent, in this tab.
    // There is no sample voucher flow to fall back to (Juan, 2026-10-13).
    { href: voucherHref, label: "Voucher flow", icon: Users, disabledReason: voucherReason },
    { href: atsReason ? null : FEATHER_ATS_PATH, label: "ATS integration", icon: LayoutDashboard, disabledReason: atsReason },
  ];

  return (
    <nav aria-label={`${FEATHER_PARTNER_NAME} preview views`} className="feather-flow-switcher relative z-10 border-b border-slate-200 bg-white">
      {/* Wraps instead of scrolling sideways: at about 800px wide the run's
          details (the voucher address the presenter has to type) were cut off
          (UAT walk, 2026-10-14). Below lg they take their own row. */}
      <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-2 px-4 py-3 sm:px-8">
        <span className="mr-2 hidden shrink-0 text-xs font-semibold uppercase tracking-[0.16em] text-slate-400 md:inline">
          {run ? "Live demo" : "Preview"}
        </span>
        {views.map(({ href, label, icon: Icon, disabledReason }) => {
          const active = label === "Applicant flow"
            ? pathname.startsWith(FEATHER_APPLICANT_PATH)
            : label === "Voucher flow"
              ? pathname === FEATHER_VOUCHER_PATH
              : pathname === FEATHER_ATS_PATH;
          if (!href) {
            return (
              <span
                key={label}
                role="link"
                aria-disabled="true"
                tabIndex={0}
                // An explicit name: in Chrome the `title` became the whole
                // name, so a screen reader heard the reason without the tab
                // (UAT walk, 2026-10-14).
                aria-label={disabledReason ? `${label}: ${disabledReason}` : label}
                title={disabledReason ?? undefined}
                className="inline-flex shrink-0 cursor-not-allowed items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-4 py-2 text-sm font-semibold text-slate-400"
              >
                <Icon className="h-4 w-4" aria-hidden="true" />
                {label}
              </span>
            );
          }
          return (
            <Link
              key={label}
              to={href}
              aria-current={active ? "page" : undefined}
              className={`inline-flex shrink-0 items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${
                active
                  ? "border-[#1859c9] bg-[#1859c9] text-white shadow-sm"
                  : "border-slate-200 bg-white text-slate-600 hover:border-[#9fbaef] hover:bg-[#f7f9ff] hover:text-[#1554bb]"
              }`}
            >
              <Icon className="h-4 w-4" aria-hidden="true" />
              {label}
            </Link>
          );
        })}
        <div className="flex basis-full flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500 lg:ml-auto lg:basis-auto lg:pl-4">
          {run && voucherReason && <span aria-hidden="true">Voucher flow: {voucherReason}</span>}
          {run?.voucher_email && (
            <span>
              Voucher email for this run: <span className="font-semibold text-slate-700">{run.voucher_email}</span>
            </span>
          )}
          {run && (
            <button
              type="button"
              onClick={resetDemo}
              className="inline-flex items-center gap-1 font-semibold text-[#1554bb] underline-offset-4 hover:underline"
            >
              <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" /> Reset demo
            </button>
          )}
          <Link
            to={FEATHER_DEMO_SETUP_PATH}
            aria-current={pathname === FEATHER_DEMO_SETUP_PATH ? "page" : undefined}
            className="inline-flex items-center gap-1 font-semibold text-[#1554bb] underline-offset-4 hover:underline"
          >
            <Settings2 className="h-3.5 w-3.5" aria-hidden="true" /> Demo setup
          </Link>
        </div>
      </div>
    </nav>
  );
}
